import type { Column } from '@vd/types';
import { Globe, Users } from 'lucide-react';
import styles from './IntakeBoardDrawing.module.css';

/** Work columns drawn before the rest are summed up as "and N more". */
const SHOWN_WORK_COLUMNS = 3;

/**
 * What bug intake does, drawn as the project's own board (TOADO-584).
 *
 * The words on the tab explain it; this shows it. The intake column at the
 * left with a visitor's report in it, and the team's own capture landing in
 * the first work column, under the project's real column names so it reads
 * as their board rather than a diagram. While intake is off the intake column
 * is outlined, as the column that turning it on adds.
 *
 * Built from boxes on the theme's own tokens, so it follows light and dark.
 * A picture of the text beside it: hidden from a screen reader, which reads
 * the text.
 */
export function IntakeBoardDrawing({ columns, intakeEnabled, intakeColumnName }: {
  /** The project's columns, in board order; undefined while they load, when nothing is drawn. */
  columns: Column[] | undefined;
  intakeEnabled: boolean;
  /** The intake column's real name while it is on and still exists. */
  intakeColumnName: string | null;
}) {
  if (!columns) return null;
  const work = columns.filter((column) => !column.isIntake);
  const shown = work.slice(0, SHOWN_WORK_COLUMNS);
  const hidden = work.length - shown.length;
  const intakeLive = intakeEnabled && intakeColumnName !== null;
  // On, but its column was deleted: reports fall back to the first work
  // column (the tab says so in red), and the drawing must not call the
  // intake column one that turning it on adds (review).
  const intakeMissing = intakeEnabled && intakeColumnName === null;

  return (
    <div className={styles.board} aria-hidden="true" data-testid="intake-board-drawing">
      <div className={intakeLive ? styles.intakeColumn : styles.intakeColumnPending}>
        <div className={styles.columnHead}>{intakeLive ? intakeColumnName : 'Bug intake'}</div>
        <div className={styles.visitorCard}>
          <Globe size={12} />
          <span>A visitor&rsquo;s report</span>
        </div>
        {!intakeLive && (
          <div className={styles.pendingNote}>{intakeMissing ? 'Its column was deleted' : 'Added when this is on'}</div>
        )}
      </div>
      {shown.map((column, index) => (
        <div key={column.id} className={styles.column}>
          <div className={styles.columnHead}>{column.name}</div>
          {index === 0 ? (
            <>
              <div className={styles.teamCard}>
                <Users size={12} />
                <span>Your team&rsquo;s capture</span>
              </div>
              {/* Off, the visitor's report lands here with the team's work. */}
              {!intakeLive && (
                <div className={styles.visitorCardMuted}>
                  <Globe size={12} />
                  <span>Visitors&rsquo; reports, today</span>
                </div>
              )}
            </>
          ) : (
            <div className={styles.placeholderCard} />
          )}
        </div>
      ))}
      {hidden > 0 && <div className={styles.more}>and {hidden} more</div>}
    </div>
  );
}
